/**
 * ProductForm - Formulario de asignación de cantidades por producto
 *
 * Permite al usuario asignar cantidades específicas a productos individuales
 * durante el proceso de packing. Incluye:
 * - Selector de producto con búsqueda
 * - Control de cantidad con stepper y botón máximo
 * - Validación de cantidades disponibles
 * - Integración con localStorage para persistir asignaciones
 * - Navegación entre productos de la orden
 */

import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@shared/components/ui/input';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag, faSearch } from '@fortawesome/pro-light-svg-icons';
import { CommonSortDropdown, ProductRow } from '@modules/OrderPreparation/shared';
import { LocationsModal } from '../modals';
import { usePackingContext } from '../../hooks';
import { type ProductInfo, type Location } from '@modules/OrderPreparation/shared';

export const ProductForm = () => {
  const { t } = useTranslation('packingModule');
  const {
    data,
    filteredProducts,
    searchTerm,
    setSearchTerm,
    sortType,
    handleSort,
    getProductLocations,
    loading,
    error,
    orders, // Agregar orders para verificar status_id
  } = usePackingContext();

  // Estado para el modal de ubicaciones
  const locationsModalRef = useRef<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfo | undefined>(undefined);
  const [productLocations, setProductLocations] = useState<Location[]>([]);

  // Handler para abrir el modal de ubicaciones
  const handleLocationModalOpen = async (product: any) => {
    try {
      // Obtener ubicaciones reales desde la API
      const productId = parseInt(product.id);
      const locations = await getProductLocations(productId);

      // Convertir a ProductInfo para el modal
      const productInfo: ProductInfo = {
        id: product.id,
        name: product.name,
        code: product.code,
        image: product.image,
      };

      setSelectedProduct(productInfo);
      setProductLocations(locations);
      locationsModalRef.current?.openModal();
    } catch {
      // Error al obtener ubicaciones - mostrar ubicaciones vacías
      const productInfo: ProductInfo = {
        id: product.id,
        name: product.name,
        code: product.code,
        image: product.image,
      };
      setSelectedProduct(productInfo);
      setProductLocations([]);
      locationsModalRef.current?.openModal();
    }
  };

  // Mostrar loading si está cargando
  if (loading) {
    return (
      <SectionBox>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-complementary-text">{t('loading.message')}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  // Mostrar error si hay error
  if (error) {
    return (
      <SectionBox>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-error mb-2">Error</p>
            <p className="text-complementary-text text-xs">{error}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  // Mostrar mensaje si no hay datos
  if (!data) {
    return (
      <SectionBox>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-complementary-text text-xs">{t('products.no_products')}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  return (
    <SectionBox>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon icon={faShoppingBag} className="w-4 h-4 text-icons" />
        <h3 className="font-medium text-title">{t('products.title')}</h3>
      </div>

      {/* Search and Sort */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          {/* Buscar */}
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-icons"
          />
          <Input
            type="text"
            placeholder={t('products.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-8 bg-transparent"
            data-testid="packing-products-search"
          />
        </div>
        {/* Ordenar */}
        <CommonSortDropdown
          onSortChange={handleSort}
          dataTestId="packing-products-sort"
          namespace="packingModule"
          selectedSortType={sortType}
        />
      </div>

      {/* Products List - Lista de productos (altura cuando estado completo) */}
      <div className="space-y-0 max-h-[calc(100vh-215px)] overflow-y-auto">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-complementary-text text-xs">{t('products.no_products')}</p>
          </div>
        ) : (
          filteredProducts.map((product, index) => {
            // Si la preparación está completa, mostrar todo como completo
            const isPreparationComplete = data?.preparation?.status === 3;

            // Verificar si el producto está completado en pedidos terminados (status_id = 6)
            const completedQuantityFromFinishedOrders =
              orders?.reduce((total, order: any) => {
                if (order.status_id !== 6) return total; // Solo pedidos terminados

                let orderCompletedQty = 0;
                (order.lines || []).forEach((line: any) => {
                  if (line.pack && line.pack.lines) {
                    // Es un pack - verificar cada producto del pack
                    line.pack.lines.forEach((packLine: any) => {
                      if (packLine.product?.code === product.code) {
                        orderCompletedQty += packLine.qty || 0;
                      }
                    });
                  } else if (line.product && line.product.code === product.code) {
                    // Es un producto individual
                    orderCompletedQty += line.qty || 0;
                  }
                });

                return total + orderCompletedQty;
              }, 0) || 0;

            const processed = isPreparationComplete
              ? product.expectedQuantity
              : Math.min(completedQuantityFromFinishedOrders, product.expectedQuantity);
            const isCompleted = processed >= product.expectedQuantity;

            return (
              <ProductRow
                key={product.id}
                product={{
                  ...product,
                  processedQuantity: processed,
                  isCompleted,
                }}
                onLocationClick={handleLocationModalOpen}
                showSeparator={index < filteredProducts.length - 1}
                dataTestIdPrefix="packing-product"
              />
            );
          })
        )}
      </div>

      {/* Modal de ubicaciones */}
      <LocationsModal ref={locationsModalRef} product={selectedProduct} locations={productLocations} />
    </SectionBox>
  );
};
